/**
 * 3 - Disable Enemy Aircraft (03 §2): 2-5 parked aircraft on a crude strip, wingtip to
 * wingtip, ringed by unbroken wire that must be breached. Knife only until inside the wire;
 * if the garrison reaches alert 2 before then the jets scramble (30 s) and the mission fails.
 */

import { checker, clutter, guardsAbout, insideRect, ruleOf, structure, targetsOf } from './helpers';
import type { ArchetypeSpec } from './types';

/** Wingspan 9 m: 11.5 m centres leave a gap a single bomb can straddle. */
const SPACING = 11.5;

const aircraftFor = (threat: number): number => Math.min(5, Math.max(2, 2 + Math.floor((threat - 1) / 2)));

export const enemyAircraft: ArchetypeSpec = {
  id: 'enemy-aircraft',
  name: 'DISABLE ENEMY AIRCRAFT',
  nativeBiome: 'arctic',
  baseThreat: 4,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: true,
  layout: 'compound',
  size: { w: [44, 66], d: [30, 32] },
  sizeFor: (threat) => ({ w: Math.max(44, Math.ceil(aircraftFor(threat) * SPACING + 10)), d: 32 }),
  river: false,
  patrolLoop: 'inside',
  breach: { min: 1, max: 3 },

  build(g) {
    const count = aircraftFor(g.threat);
    const z = Math.round((g.rect.z0 + g.rect.z1) / 2) - 3;
    const x0 = (g.rect.x0 + g.rect.x1) / 2 - ((count - 1) * SPACING) / 2;
    // The strip runs east-west in front of the noses.
    g.map.road([[g.rect.x0 + 3, z + 11], [g.rect.x1 - 3, z + 11]], 7);
    const ids: number[] = [];
    for (let i = 0; i < count; i++) {
      const plane = g.map.place('aircraft', Math.round((x0 + i * SPACING) * 2) / 2, z, { margin: 0.4, target: true });
      if (plane) ids.push(plane.id);
      else g.map.problems.push('enemy-aircraft: no room for an aircraft');
    }
    structure(g, 'hut', { margin: 1.2 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'barrel', 3);
    clutter(g, 'crate', 2);
    guardsAbout(g, 2 + g.rng.int(2));

    const inside = { ...g.rect };
    return {
      goalZone: inside,
      objective: {
        kind: 'destroyTargets', label: 'DISABLE AIRCRAFT', targetPropIds: ids, minFraction: 1,
        rules: [
          { type: 'quietUntilZone', zone: inside, breach: 'penalty' },
          { type: 'alertConsequence', level: 2, delaySeconds: 30, unlessInsideZone: inside, effect: 'jetsScramble' },
          { type: 'noContactBonus' },
          { type: 'recall', mode: 'normal' },
        ],
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('enemy-aircraft');
    const planes = targetsOf(def, 'aircraft');
    check(planes.length >= 2 && planes.length <= 5, `needs 2-5 aircraft, has ${planes.length}`);
    check(planes.length === aircraftFor(def.threat), 'aircraft count must follow the threat rating');
    const quiet = ruleOf(def, 'quietUntilZone');
    check(quiet?.breach === 'penalty', 'needs a quiet-until-inside-the-wire rule with a penalty');
    const scramble = ruleOf(def, 'alertConsequence');
    check(
      scramble?.effect === 'jetsScramble' && scramble.level === 2 && scramble.delaySeconds === 30,
      'jets must scramble 30 s after alert 2',
    );
    check(
      scramble?.unlessInsideZone !== undefined,
      'the scramble must be called off once the ranger is inside the wire',
    );
    check(ruleOf(def, 'noContactBonus') !== undefined, 'offers the no-contact bonus');
    for (const p of planes) {
      if (quiet) check(insideRect(quiet.zone, p.x, p.z), `aircraft ${p.id} stands outside the wire`);
      check(probe.breach(p.id) >= 1, `the wire ring is not closed: aircraft ${p.id} can be reached without a breach`);
    }
    return out;
  },
};
